import React from "react";
import styled from "styled-components";
import Text from "../../atoms/Text";
import Button from "../../atoms/Button";
import Tag from "../../atoms/Tag";
import Image from "../../atoms/Image";
import { DrugIdentificationStatus } from "../../../common/models/types";
import { DrugIdentificationWithUser, getDrugIdentificationImageUrl } from "../../../api/drugIdentificationService";

// 표시용: string | string[] → string
const formatIdentifyText = (value?: string | string[]): string => {
  if (!value) return '';
  return Array.isArray(value) ? value.join(', ') : value;
};

// 선택된 약품 정보 찾기
const getSelectedDrug = (item: DrugIdentificationWithUser) => {
  if (item.status !== DrugIdentificationStatus.SELECTED || !item.selectedItemCode) {
    return null;
  }
  return item.results?.find(r => r.itemSeq === item.selectedItemCode) || null;
};

// 검색 조건 타입 (슬라이드 패널에 전달)
export interface SearchCondition {
  identifyText1?: string | string[];
  identifyText2?: string | string[];
  formulation?: string[];
  shape?: string[];
  color?: string[];
  line?: string[];
}

interface Props {
  identifications: DrugIdentificationWithUser[];
  loading: boolean;
  currentPage: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onViewDetail: (id: number) => void;
  onDelete: (item: DrugIdentificationWithUser) => void;
  onConditionDoubleClick?: (condition: SearchCondition) => void;
}

const TableContainer = styled.div`
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  width: 100%;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
`;

const Thead = styled.thead`
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  border-bottom: 1px solid #e5e7eb;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f9fafb;
  }
`;

const Th = styled.th`
  padding: 12px 16px;
  text-align: left;
  font-weight: 600;
  font-size: 14px;
  color: #374151;
  white-space: nowrap;

  &:nth-child(1) { width: 5%; }  /* ID */
  &:nth-child(2) { width: 12%; } /* 검색조건 */
  &:nth-child(3) { width: 18%; } /* 이미지 */
  &:nth-child(4) { width: 28%; } /* 상태 */
  &:nth-child(5) { width: 12%; } /* 사용자 */
  &:nth-child(6) { width: 10%; } /* 생성일 */
  &:nth-child(7) { width: 15%; } /* 액션 */
`;

const ConditionSummary = styled.div<{ $clickable?: boolean }>`
  font-size: 12px;
  color: #6b7280;
  line-height: 1.4;
  cursor: ${props => props.$clickable ? 'pointer' : 'default'};
  padding: 4px;
  border-radius: 4px;
  transition: background-color 0.15s;

  ${props => props.$clickable && `
    &:hover {
      background-color: #f3f4f6;
    }
  `}
`;

const Td = styled.td`
  padding: 12px 16px;
  font-size: 14px;
  color: #374151;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ThumbnailContainer = styled.div`
  display: flex;
  gap: 4px;

  img {
    max-height: 80px;
    width: auto;
    object-fit: contain;
  }
`;

const SelectedDrugImage = styled.div`
  margin-top: 6px;

  img {
    max-height: 48px;
    width: auto;
    object-fit: contain;
    border-radius: 4px;
  }
`;


const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const EmptyState = styled.div`
  padding: 60px 20px;
  text-align: center;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  padding: 20px;
  border-top: 1px solid #e5e7eb;
`;

const PageButton = styled.button<{ active?: boolean }>`
  padding: 6px 12px;
  border: 1px solid ${(props) => (props.active ? "#4A90E2" : "#e5e7eb")};
  background: ${(props) => (props.active ? "#4A90E2" : "#fff")};
  color: ${(props) => (props.active ? "#fff" : "#374151")};
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;

  &:hover:not(:disabled) {
    border-color: #4a90e2;
    color: ${(props) => (props.active ? "#fff" : "#4A90E2")};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const PageInfo = styled(Text)`
  margin: 0 16px;
  font-size: 14px;
  color: #6b7280;
`;

// 상태별 태그 색상
const getStatusTagColor = (status: DrugIdentificationStatus): "Green" | "Orange" | "Red" | "Blue" | "Gray" => {
  switch (status) {
    case DrugIdentificationStatus.COMPLETED:
      return "Green";
    case DrugIdentificationStatus.SELECTED:
      return "Blue";
    case DrugIdentificationStatus.PROCESSING:
      return "Orange";
    case DrugIdentificationStatus.PENDING:
      return "Gray";
    case DrugIdentificationStatus.FAILED:
    case DrugIdentificationStatus.WRONG:
      return "Red";
    default:
      return "Gray";
  }
};

// 상태 텍스트
const getStatusText = (status: DrugIdentificationStatus): string => {
  switch (status) {
    case DrugIdentificationStatus.COMPLETED: return '완료';
    case DrugIdentificationStatus.PROCESSING: return '처리중';
    case DrugIdentificationStatus.FAILED: return '실패';
    case DrugIdentificationStatus.PENDING: return '대기';
    case DrugIdentificationStatus.SELECTED: return '선택됨';
    case DrugIdentificationStatus.WRONG: return '오류';
    default: return status;
  }
};

// 시간을 분:초 형식으로 변환
const formatDuration = (milliseconds: number | undefined): string => {
  if (!milliseconds) return "-";

  const totalSeconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  if (minutes > 0) {
    return `${minutes}분 ${seconds}초`;
  }
  return `${seconds}초`;
};

export const AdminDrugIdentificationTable: React.FC<Props> = ({
  identifications,
  loading,
  currentPage,
  pageSize,
  total,
  onPageChange,
  onViewDetail,
  onDelete,
  onConditionDoubleClick,
}) => {
  const totalPages = Math.ceil(total / pageSize);

  // 페이지 번호 생성
  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage < maxPagesToShow - 1) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  };

  if (loading && identifications.length === 0) {
    return (
      <TableContainer>
        <EmptyState>
          <Text>데이터를 불러오는 중...</Text>
        </EmptyState>
      </TableContainer>
    );
  }

  if (identifications.length === 0) {
    return (
      <TableContainer>
        <EmptyState>
          <Text>조회된 의약품 식별 이력이 없습니다.</Text>
        </EmptyState>
      </TableContainer>
    );
  }

  return (
    <TableContainer>
      <Table>
        <Thead>
          <Tr>
            <Th>ID</Th>
            <Th>검색조건</Th>
            <Th>이미지</Th>
            <Th>상태</Th>
            <Th>사용자</Th>
            <Th>생성일</Th>
            <Th>액션</Th>
          </Tr>
        </Thead>
        <Tbody>
          {identifications.map((item) => (
            <Tr key={item.id}>
              <Td>{item.id}</Td>
              {/* 검색조건 */}
              <Td>
                <ConditionSummary
                  $clickable={!!onConditionDoubleClick}
                  onDoubleClick={() => {
                    if (onConditionDoubleClick && item.conditions) {
                      onConditionDoubleClick({
                        identifyText1: item.conditions.identifyText1,
                        identifyText2: item.conditions.identifyText2,
                        formulation: item.conditions.formulation,
                        shape: item.conditions.shape,
                        color: item.conditions.color,
                        line: item.conditions.line,
                      });
                    }
                  }}
                  title={onConditionDoubleClick ? "더블클릭하여 검색 패널 열기" : undefined}
                >
                  {item.conditions?.identifyText1 && (
                    <div>문자: {formatIdentifyText(item.conditions.identifyText1)}{item.conditions.identifyText2 ? `, ${formatIdentifyText(item.conditions.identifyText2)}` : ''}</div>
                  )}
                  {item.conditions?.shape?.length > 0 && (
                    <div>모양: {item.conditions.shape.join(', ')}</div>
                  )}
                  {item.conditions?.color?.length > 0 && (
                    <div>색상: {item.conditions.color.join(', ')}</div>
                  )}
                  {!item.conditions?.identifyText1 && !item.conditions?.shape?.length && !item.conditions?.color?.length && '-'}
                </ConditionSummary>
              </Td>
              {/* 이미지 */}
              <Td>
                <ThumbnailContainer>
                  <Image
                    src={getDrugIdentificationImageUrl(item.id!, 0)}
                    alt="이미지 1"
                    height={80}
                    objectFit="contain"
                    enableHoverZoom={true}
                    zoomScale={6}
                    showOnError={false}
                  />
                  <Image
                    src={getDrugIdentificationImageUrl(item.id!, 1)}
                    alt="이미지 2"
                    height={80}
                    objectFit="contain"
                    enableHoverZoom={true}
                    zoomScale={6}
                    showOnError={false}
                  />
                </ThumbnailContainer>
              </Td>
              {/* 상태 */}
              <Td>
                {(() => {
                  const selectedDrug = getSelectedDrug(item);
                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', alignItems: 'center' }}>
                        {item.deletedAt ? (
                          <Tag variant="Tinted" color="Gray" size="Small">
                            삭제
                          </Tag>
                        ) : (
                          <Tag variant="Tinted" color={getStatusTagColor(item.status)} size="Small">
                            {getStatusText(item.status)}
                          </Tag>
                        )}
                        {selectedDrug && (
                          <Text textType="Caption" color="#059669" style={{ fontWeight: 500 }}>
                            {selectedDrug.itemName}
                          </Text>
                        )}
                      </div>
                      {selectedDrug?.itemImage && (
                        <SelectedDrugImage>
                          <Image
                            src={selectedDrug.itemImage}
                            alt={selectedDrug.itemName}
                            height={48}
                            objectFit="contain"
                            enableHoverZoom={true}
                            zoomScale={8}
                            showOnError={false}
                          />
                        </SelectedDrugImage>
                      )}
                    </div>
                  );
                })()}
              </Td>
              {/* 사용자 */}
              <Td>
                <div>
                  <Text textType="Caption" color="#374151">
                    {item.user?.name || '-'}
                  </Text>
                </div>
                <Text textType="Caption" color="#6b7280">
                  {item.user?.email || '-'}
                </Text>
              </Td>
              {/* 생성일 */}
              <Td>
                {item.createdAt ? new Date(item.createdAt).toLocaleDateString("ko-KR") : '-'}
              </Td>
              {/* 액션 */}
              <Td>
                <ActionButtons>
                  <Button
                    size="Small"
                    type="Text"
                    variant="Contained"
                    color="Primary"
                    onClick={() => onViewDetail(item.id!)}
                  >
                    상세
                  </Button>
                  {!item.deletedAt && (
                    <Button
                      size="Small"
                      type="Text"
                      variant="Outlined"
                      color="Negative"
                      onClick={() => onDelete(item)}
                    >
                      삭제
                    </Button>
                  )}
                </ActionButtons>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>

      {totalPages > 1 && (
        <PaginationContainer>
          <PageButton onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1}>
            이전
          </PageButton>

          {getPageNumbers().map((page) => (
            <PageButton key={page} active={page === currentPage} onClick={() => onPageChange(page)}>
              {page}
            </PageButton>
          ))}

          <PageInfo>
            {currentPage} / {totalPages} 페이지
          </PageInfo>

          <PageButton
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            다음
          </PageButton>
        </PaginationContainer>
      )}
    </TableContainer>
  );
};
